import { Message, MessageImageGroup } from "@/components/ui/message"
import { MessageGroup } from "@/reducers/messagesSlice"
import { useMessageData } from "@/components/chat/useMessageData"
import { useGroupedFiles } from "@/components/chat/useGroupedFiles"
import { memo, useContext } from "react"
import { ChatHistoryContext } from "@/contexts/ChatHistoryContext"
import useGroupedMessageActions from "./useGroupedMessageActions"

type GroupedImagesMessageProps = {
  messagesGroup: MessageGroup
  interactive?: boolean
  actionButtonsVisible?: boolean
  hideActionButtons?: () => void
}

const GroupedImagesMessage: React.FC<GroupedImagesMessageProps> = ({
  messagesGroup,
  interactive = true,
  actionButtonsVisible,
  hideActionButtons,
}) => {
  const messageForRowAction = messagesGroup.messages[0]
  const messageData = useMessageData(messageForRowAction)
  const actions = useGroupedMessageActions(messagesGroup)

  const images = useGroupedFiles(messagesGroup.messages, messageData.authorName)

  const { expandImageGroup, isForwardingMode } = useContext(ChatHistoryContext)

  const handleExpand = () => {
    expandImageGroup(messagesGroup)
  }

  return (
    <Message
      message={messageForRowAction}
      variant={messageData.variant}
      showAvatar={!isForwardingMode}
      interactive={interactive}
      onForward={!isForwardingMode ? actions.handleForward : undefined}
      actionButtonsVisible={actionButtonsVisible}
      hideActionButtons={hideActionButtons}
    >
      <Message.Avatar
        src={messageData.avatarSrc}
        name={messageData.authorName}
        fallback={messageData.authorInitials}
        avatarColor={messageData.avatarColor}
      />
      <Message.Bubble withDriver={messageData.isFromDriver}>
        <Message.Header author={messageData.authorName} showTimestamp={false} />
        <MessageImageGroup
          images={images}
          maxImages={4}
          gap="md"
          aspectRatio="square"
          onExpand={handleExpand}
        />
      </Message.Bubble>
      {interactive && !isForwardingMode && <Message.Actions />}
    </Message>
  )
}

// Custom comparison function that ignores context changes that don't affect rendering
const areEqual = (
  prevProps: GroupedImagesMessageProps,
  nextProps: GroupedImagesMessageProps,
) => {
  // Compare message group properties
  return (
    prevProps.messagesGroup.groupId === nextProps.messagesGroup.groupId &&
    prevProps.messagesGroup.messages.length ===
      nextProps.messagesGroup.messages.length &&
    prevProps.messagesGroup.messages.every(
      (msg, index) => msg.id === nextProps.messagesGroup.messages[index]?.id,
    ) &&
    prevProps.interactive === nextProps.interactive &&
    prevProps.actionButtonsVisible === nextProps.actionButtonsVisible
  )
}

export default memo(GroupedImagesMessage, areEqual)
